import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { api, ApiError } from "../lib/api";

type Contact = {
  id: number;
  first_name: string;
  company: string | null;
  email: string;
  phone: string | null;
  topic: string | null;
  message: string;
  attachment_url: string | null;
  attachment_name: string | null;
  attachments: { url: string; name: string; size?: number }[] | null;
  locale: string | null;
  ip: string | null;
  user_agent: string | null;
  handled: boolean;
  handled_at: string | null;
  handled_by: number | null;
  handled_by_email: string | null;
  created_at: string;
};

/** 单个「标签 + 值」单元 */
function Cell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="muted" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{label}</div>
      <div>{children}</div>
    </div>
  );
}

/** 一行两列：左右各一个「标签 + 值」 */
function RowPair({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32, padding: "12px 0", borderBottom: "1px solid var(--border)" }}>
      {children}
    </div>
  );
}

export function ContactsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [data, setData] = useState<Contact | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState(false);

  const load = () => {
    setError(null);
    api.get<Contact>(`/api/admin/contacts/${id}`).then(setData).catch((e) => setError(String(e)));
  };

  useEffect(load, [id]);

  const toggle = async () => {
    if (!data) return;
    setActing(true);
    try {
      const updated = await api.patch<Contact>(`/api/admin/contacts/${data.id}`, { handled: !data.handled });
      setData(updated);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.status}: ${JSON.stringify(e.body)}` : String(e));
    } finally {
      setActing(false);
    }
  };

  if (error) return <div className="error">{error}</div>;
  if (!data) return <div className="muted">加载中…</div>;

  return (
    <div>
      <div className="page-header">
        <div className="page-title">咨询详情 #{data.id}</div>
        <div className="page-actions">
          <button className="btn" onClick={() => nav("/contacts")}>← 返回列表</button>
          <button
            className={`btn ${data.handled ? "" : "btn-primary"}`}
            onClick={toggle}
            disabled={acting}
          >
            {acting ? "处理中…" : (data.handled ? "撤销已处理" : "标记已处理")}
          </button>
        </div>
      </div>

      <div className="form">
        <RowPair>
          <Cell label="姓名">{data.first_name}</Cell>
          <Cell label="机构 / 公司">{data.company || <span className="muted">—</span>}</Cell>
        </RowPair>
        <RowPair>
          <Cell label="邮箱"><a href={`mailto:${data.email}`}>{data.email}</a></Cell>
          <Cell label="电话">{data.phone || <span className="muted">—</span>}</Cell>
        </RowPair>
        <RowPair>
          <Cell label="身份">{data.topic || <span className="muted">—</span>}</Cell>
          <Cell label="提交时间">{new Date(data.created_at).toLocaleString()}</Cell>
        </RowPair>
        <RowPair>
          <Cell label="来源 IP">{data.ip || <span className="muted">—</span>}</Cell>
          <Cell label="处理状态">
            {data.handled ? (
              <span>
                <span className="muted">已处理</span>
                {data.handled_at && <span className="muted" style={{ marginLeft: 8 }}>· {new Date(data.handled_at).toLocaleString()}</span>}
                {data.handled_by_email && <span className="muted" style={{ marginLeft: 8 }}>· {data.handled_by_email}</span>}
              </span>
            ) : (
              <span className="success">● 未处理</span>
            )}
          </Cell>
        </RowPair>
        <div style={{ padding: "12px 0" }}>
          <Cell label="附件">
            {(() => {
              // 优先用 attachments 数组；旧记录降级到单字段
              const list = data.attachments && data.attachments.length > 0
                ? data.attachments
                : data.attachment_url
                  ? [{ url: data.attachment_url, name: data.attachment_name || data.attachment_url }]
                  : [];
              if (list.length === 0) return <span className="muted">无</span>;
              return (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 2 }}>
                  {list.map((a, i) => (
                    <a key={i} href={a.url} target="_blank" rel="noopener noreferrer" className="slug">
                      {a.name} ↗{a.size ? <span className="muted" style={{ marginLeft: 8 }}>{(a.size / 1e6).toFixed(1)} MB</span> : null}
                    </a>
                  ))}
                </div>
              );
            })()}
          </Cell>
        </div>
      </div>

      {/* 留言：移到最下方，多行展示 */}
      <div className="form" style={{ marginTop: 16 }}>
        <div className="ce-label" style={{ marginBottom: 10 }}>留言内容</div>
        <div className="detail-message">{data.message}</div>
      </div>
    </div>
  );
}
